import React, { useState, useEffect } from "react";
import {
  Box,
  Heading,
  Text,
  Button,
  Table,
  TableHeader,
  TableRow,
  TableCell,
  TableBody,
  RangeInput,
} from "grommet";
import API_URL from "../../config/config";
import { Info, LinkPrevious } from "grommet-icons";
import { useNavigate } from "react-router-dom";
import BreadcrumbNavigation from "./BreadcrumbNavigation2";
import adminAxios from "../../config/adminAxios";

export const QuizFields = ({ onSubmit, data, assessment_id }) => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [skills, setSkills] = useState([]);
  const [isLoadingSkills, setIsLoadingSkills] = useState(true);
  const [skillsError, setSkillsError] = useState(null);
  const [skillQuestionCounts, setSkillQuestionCounts] = useState([]);
  const [submittedData, setSubmittedData] = useState(null);
  const userId = data;
  const a_id = assessment_id.assessment_id;
  const courseSelectionStorageKey = `skillCourseSelections:${a_id}`;

  const getMin = () => {
    return skills.length * 5; // Ensure a minimum of 5 questions per skill
  };

  const getMax = () => {
    return 10 * skills.length; // Maximum total questions based on skills length
  };

  const initializeSkillQuestionCounts = () => {
    const numSkills = skills.length;
    const initialValue = 5; // Starting each skill with a minimum of 5 questions
    const remainder = getMin() % numSkills;

    const initialCounts = Array(numSkills).fill(initialValue);
    for (let i = 0; i < remainder; i++) {
      initialCounts[i] += 1;
    }

    setSkillQuestionCounts(initialCounts);
  };

  const handleSkillSliderChange = (index, newValue) => {
    const currentTotal = skillQuestionCounts.reduce((a, b) => a + b, 0);
    const maxTotal = getMax();
    const minTotal = getMin();
    const remainingSkills = skills.length - 1;

    const maxAllowedForSkill = Math.min(10, maxTotal - remainingSkills);
    let newCounts = [...skillQuestionCounts];

    // Prevent values lower than 5
    if (newValue < 5) {
      return;
    }

    // Check if decreasing the slider would bring total below minTotal
    const newTotalWithDecreasedValue =
      currentTotal - (skillQuestionCounts[index] - newValue);

    if (newTotalWithDecreasedValue < minTotal) {
      return;
    }

    // Ensure the selected skill does not exceed the allowed max (and max 10 per skill)
    newCounts[index] = Math.min(newValue, maxAllowedForSkill);

    const newTotal = newCounts.reduce((a, b) => a + b, 0);

    // If the total exceeds the max allowed, adjust other sliders
    if (newTotal > maxTotal) {
      let remainingDelta = newTotal - maxTotal;

      for (let i = 0; i < newCounts.length; i++) {
        if (i !== index && remainingDelta > 0) {
          const adjustableAmount = Math.min(remainingDelta, newCounts[i] - 5); // Ensure at least 5 questions remain
          newCounts[i] -= adjustableAmount;
          remainingDelta -= adjustableAmount;
        }
      }
    }

    setSkillQuestionCounts(newCounts);
  };

  const handleSubmit = () => {
    setIsSubmitting(true);

    let courseSelectionsBySkill = {};
    try {
      courseSelectionsBySkill =
        JSON.parse(localStorage.getItem(courseSelectionStorageKey)) || {};
    } catch (error) {
      console.error("Error reading course selections:", error);
    }

    const skillDistribution = skills.map((skill, index) => {
      const selectedCourses = courseSelectionsBySkill[skill.sub_product_id];
      const courseIds = Array.isArray(selectedCourses)
        ? selectedCourses
            .map((courseId) => Number(courseId))
            .filter((courseId) => Number.isInteger(courseId))
        : [];
      return {
        skillId: skill.sub_product_id,
        count: skillQuestionCounts[index] || 0,
        ...(courseIds.length > 0 && { courseIds }),
      };
    });

    setSubmittedData(skillDistribution);

    onSubmit({
      totalQuestions: skillQuestionCounts.reduce((a, b) => a + b, 0),
      skillDistribution,
    });

    setIsSubmitting(false);
  };
  useEffect(() => {
    const fetchSkills = async () => {
      try {
        
        const response = await adminAxios.get(
          `${API_URL}/manager/getUserProductSkills/${a_id}`,
        );
        setSkills(response.data.skills);
        setIsLoadingSkills(false);
      } catch (error) {
        setSkillsError(error.message);
        setIsLoadingSkills(false);
      }
    };

    fetchSkills();
  }, [userId]);

  useEffect(() => {
    if (skills.length > 0) {
      initializeSkillQuestionCounts();
    }
  }, [skills]);

  const totalQuestions = skillQuestionCounts.reduce((a, b) => a + b, 0); // Calculate total questions

  return (
    <Box flex="grow" pad="medium">
      <Box
        margin={{ bottom: "small" }}
        pad="small"
        direction="row"
        align="center"
        justify="between"
        // background="light-2"
        // round="small"
        // elevation="small"
        height="50px"
      >
        {/* Left side: Back Arrow + Title */}
        <Box direction="row" align="center" gap="small">
          <LinkPrevious
            size="large"
            onClick={() => navigate(-1)}
            color="brand"
          />
          <BreadcrumbNavigation
            current={"Step 3: Select Number of Questions"}
            previous={"Select Skills"}
          />
        </Box>
      </Box>
      {/* <Box
        border={{ color: "brand", size: "xsmall" }}
        width="75%"
        alignSelf="end"
      /> */}
      <Box
        pad={{ vertical: "medium", horizontal: "large" }}
        gap="medium"
        align="center"
      >
        {isLoadingSkills ? (
          <Text>Loading skills...</Text>
        ) : skillsError ? (
          <Text color="status-critical">Error: {skillsError}</Text>
        ) : (
          <Box width="large" margin={{ bottom: "medium" }}>
            <Box
              pad="medium"
              background="light-2" // You can customize this as per your design
              round="small"
              margin="medium"
            >
              <Text weight="bold">
                <Info color="brand" /> In order to evaluate team&apos;s accurate
                skills, we recommend selecting the maximum number of questions.
              </Text>
            </Box>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableCell scope="col" border="bottom">
                    <Text weight="bold">Skill(s)</Text>
                  </TableCell>
                  <TableCell scope="col" border="bottom">
                    <Text weight="bold">Number of questions</Text>
                  </TableCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {skills.map((skill, index) => (
                  <TableRow key={skill.sub_product_id}>
                    <TableCell>
                      {skill.product_name} - {skill.sub_product_name}
                    </TableCell>
                    <TableCell>
                      <Box
                        direction="row"
                        align="center"
                        justify="between" // Use 'between' to space items evenly
                        style={{ width: "100%" }}
                      >
                        <Text>{5}</Text>
                        <Box direction="column">
                          {" "}
                          <RangeInput
                            value={skillQuestionCounts[index] || 5}
                            min={5} // Set the minimum to 5
                            max={Math.min(10, getMax() - (skills.length - 1))}
                            onChange={(e) =>
                              handleSkillSliderChange(
                                index,
                                parseInt(e.target.value, 10),
                              )
                            }
                          />
                          <Text alignSelf="center">
                            {skillQuestionCounts[index] || 5} questions
                          </Text>
                        </Box>
                        <Text>
                          {Math.min(10, getMax() - (skills.length - 1))}
                        </Text>{" "}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}

        {/* Display total number of questions */}
        {skills.length >= 2 && (
          <Text weight="bold">Total number of questions: {totalQuestions}</Text>
        )}

        {!isLoadingSkills && (
          <Box width="medium" pad="medium">
            <Button
              primary
              onClick={handleSubmit}
              label={isSubmitting ? "Starting assessment..." : "Continue"}
              disabled={isSubmitting}
            />
          </Box>
        )}

        {submittedData && (
          <Box margin={{ top: "medium" }}>
            <Heading level="4">Submitted skill distribution</Heading>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableCell scope="col">
                    <Text>Skill ID</Text>
                  </TableCell>
                  <TableCell scope="col">
                    <Text>Question Count</Text>
                  </TableCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {submittedData.map((item) => (
                  <TableRow key={item.skillId}>
                    <TableCell>{item.skillId}</TableCell>
                    <TableCell>{item.count}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Box>
    </Box>
  );
};
